import InsightsCanvasSummary from "@/components/insights/InsightsCanvasSummary";
import InsightsDetails from "@/components/insights/InsightsDetails";
import InsightsList from "@/components/insights/InsightsList";
import InsightsWaitingState from "@/components/insights/InsightsWaitingState";
import { useCanvas } from "@/hooks/useCanvas";
import { useInsights } from "@/hooks/useInsights";
import { useCanvasLayout } from "@/layouts/CanvasLayoutContext";
import { parseTopicIdSafe } from "@/stores/priorityStore";
import { CenteredLoader } from "@/ui";
import { fromMobile, useMobileBreakpoint } from "@/ui";
import { useAuth } from "@canucci/base/auth";
import { darkSurfaceDrawerStyles, leftAccentCardProps, surfaceCardProps } from "@canucci/base-mantine/theme";
import { Box, Center, Drawer, Grid, Paper, Stack, Text, useMantineTheme } from "@mantine/core";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { shouldShowVotingNotEnabled } from "./insightsAvailability";

function InsightsPage() {
  const theme = useMantineTheme();
  const { sizes, spacing, semanticColors } = theme.other;
  const { t: tPages } = useTranslation("pages");
  const { canvasResult, externalId } = useCanvas();
  const { user } = useAuth();
  const { setDarkBackground } = useCanvasLayout();
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(null);
  const isMobile = useMobileBreakpoint();

  const canvasData = canvasResult.data;
  const canvasLoading = canvasResult.isLoading;
  const canvasError = canvasResult.error;

  // Fetch insights data
  const { insights, loading, error, errorStatus, refresh } = useInsights(externalId, false, user, canvasLoading);

  useEffect(() => {
    setDarkBackground(true);
  }, [setDarkBackground]);

  const resolvedTopics = useMemo(() => {
    if (!insights || !canvasData?.themes) return [];
    const themes = canvasData.themes;

    return insights.topics.map((topic) => {
      const groupByUuid = themes.find((theme) => theme.id === topic.target_id);
      if (groupByUuid) {
        const groupTitle = groupByUuid.text.title?.trim() || tPages("canvas.insights.unknownGroup");
        const topicSummary = groupByUuid.text.summary?.trim() || groupByUuid.text.body?.trim() || undefined;
        return { ...topic, groupTitle, topicTitle: groupTitle, topicSummary };
      }

      const bySubthemeUuid = themes
        .flatMap((theme, groupIndex) =>
          (theme.subthemes ?? []).map((subtheme, areaIndex) => ({ theme, subtheme, groupIndex, areaIndex })),
        )
        .find((candidate) => candidate.subtheme.id === topic.target_id);
      if (bySubthemeUuid) {
        const groupTitle = bySubthemeUuid.theme.text.title?.trim() || tPages("canvas.insights.unknownGroup");
        const topicTitle = bySubthemeUuid.subtheme.text.title?.trim() || groupTitle;
        const topicSummary =
          bySubthemeUuid.subtheme.text.summary?.trim() || bySubthemeUuid.subtheme.text.body?.trim() || undefined;
        return { ...topic, groupTitle, topicTitle, topicSummary };
      }

      // Legacy fallback: target_id is in "groupIndex:areaIndex" format
      const parsed = parseTopicIdSafe(topic.target_id);
      if (!parsed) {
        return {
          ...topic,
          groupTitle: tPages("canvas.insights.unknownGroup"),
          topicTitle: topic.target_id,
          topicSummary: undefined,
        };
      }

      const group = themes[parsed.groupIndex];
      const groupTitle =
        group?.text.title ??
        tPages("canvas.insights.groupFallback", {
          index: parsed.groupIndex + 1,
        });
      const focusArea = group?.subthemes?.[parsed.areaIndex];
      const topicTitle = focusArea?.text.title ?? tPages("canvas.insights.topicFallback", { topicId: topic.target_id });
      const topicSummary = focusArea?.text.summary ?? focusArea?.text.body ?? undefined;

      return { ...topic, groupTitle, topicTitle, topicSummary };
    });
  }, [insights, canvasData?.themes, tPages]);

  // Auto-select first topic on desktop when data loads
  useEffect(() => {
    if (!isMobile && resolvedTopics.length > 0 && !selectedTopicId) {
      setSelectedTopicId(resolvedTopics[0].target_id);
    }
  }, [resolvedTopics, isMobile, selectedTopicId]);

  const selectedTopic = useMemo(() => {
    if (!selectedTopicId) return null;
    return resolvedTopics.find((t) => t.target_id === selectedTopicId) ?? null;
  }, [selectedTopicId, resolvedTopics]);

  // Handle topic selection - on mobile, just set the ID (drawer will open)
  const handleTopicSelect = (topicId: string) => {
    setSelectedTopicId(topicId);
  };

  // Handle drawer close on mobile
  const handleDrawerClose = () => {
    if (isMobile) {
      setSelectedTopicId(null);
    }
  };

  // Show loading while canvas is loading or insights are loading
  if (canvasLoading || loading) {
    return (
      <Box data-testid="insights-page">
        <CenteredLoader />
      </Box>
    );
  }

  // If canvas failed to load, CanvasLayout will handle showing NotFoundPage
  if (canvasError || !canvasData) {
    return null;
  }

  if (shouldShowVotingNotEnabled(errorStatus, insights)) {
    return (
      <Box data-testid="insights-page">
        <Center mih="100%">
          <Paper {...surfaceCardProps} p="xl">
            <Text c="var(--text-on-dark-muted)">{tPages("canvas.insights.votingNotEnabled")}</Text>
          </Paper>
        </Center>
      </Box>
    );
  }

  // Insights are not yet available while voting is open
  if (insights?.voting_open) {
    return (
      <Box data-testid="insights-page">
        <InsightsWaitingState onRefresh={refresh} loading={loading} />
      </Box>
    );
  }

  // Backward-compat: older API returns 403 while collecting input
  if (errorStatus === 403) {
    return (
      <Box data-testid="insights-page">
        <InsightsWaitingState onRefresh={refresh} loading={loading} />
      </Box>
    );
  }

  if (error || !insights) {
    return (
      <Box data-testid="insights-page">
        <Center mih="100%">
          <Paper {...surfaceCardProps} p="xl">
            <Text c={semanticColors.error}>
              {error ? tPages("canvas.insights.errorLoading", { error }) : tPages("canvas.insights.noData")}
            </Text>
          </Paper>
        </Center>
      </Box>
    );
  }

  const hasVotes = insights.total_votes > 0;

  return (
    <Stack mih="100%" gap={0}>
      <Box flex={1} py="xl">
        <Stack maw={sizes.maxPageWidthDesktop} mx="auto" px="xl" gap={spacing.pageGap} data-testid="insights-page">
          <InsightsCanvasSummary canvasData={canvasData} inverted />

          {!hasVotes ? (
            <Paper {...leftAccentCardProps} p="xl">
              <Text c="var(--text-on-dark)">{tPages("canvas.insights.noVotesYet")}</Text>
            </Paper>
          ) : (
            <>
              <Grid gutter={spacing.pageGap}>
                <Grid.Col span={fromMobile(12, 4.4)}>
                  <Stack gap="md">
                    <InsightsList
                      topics={resolvedTopics}
                      selectedTopicId={isMobile ? null : selectedTopicId}
                      onTopicSelect={handleTopicSelect}
                    />
                    <Box>
                      {insights.voting_open ? (
                        <Text c="var(--text-on-dark)" fz="md">
                          {tPages("canvas.insights.votingOpen", {
                            count: insights.unique_voters,
                          })}
                        </Text>
                      ) : (
                        <Text c="var(--text-on-dark)" fz="md">
                          {tPages("canvas.insights.votingClosed", {
                            count: insights.unique_voters,
                          })}
                        </Text>
                      )}
                    </Box>
                  </Stack>
                </Grid.Col>
                {!isMobile && (
                  <Grid.Col span={fromMobile(12, 7.6)}>
                    <InsightsDetails topic={selectedTopic} allTopics={resolvedTopics} isMobile={false} />
                  </Grid.Col>
                )}
              </Grid>

              {/* Mobile Drawer */}
              {isMobile && (
                <Drawer
                  opened={!!selectedTopicId}
                  onClose={handleDrawerClose}
                  position="bottom"
                  size="100%"
                  title={selectedTopic?.topicTitle}
                  styles={darkSurfaceDrawerStyles()}
                >
                  <InsightsDetails topic={selectedTopic} allTopics={resolvedTopics} isMobile={true} />
                </Drawer>
              )}
            </>
          )}
        </Stack>
      </Box>
    </Stack>
  );
}

export default InsightsPage;
